import { api, uploadFormData } from './api';
import type {
  DetectedGarment,
  Garment,
  GarmentDetectionResult,
  GarmentFilters,
  PaginatedResponse,
} from '@/types';
import {
  segmentGarmentsClient,
  warmUpSegmentation,
  type ClientSegmentationResult,
} from './clientSegmentation';

// ---------------------------------------------------------------------------
// Backend snake_case → Frontend camelCase mapper
// ---------------------------------------------------------------------------

/** Map a backend GarmentResponse to a frontend Garment. */
function mapGarment(raw: Record<string, unknown>): Garment {
  return {
    id: String(raw.id ?? ''),
    userId: String(raw.user_id ?? ''),
    name: String(raw.name ?? ''),
    category: (raw.category ?? 'other') as Garment['category'],
    subCategory: String(raw.sub_category ?? ''),
    colors: ((raw.primary_colors as string[]) ?? []).map((hex) => hex as Garment['colors'][number]),
    pattern: (raw.pattern ?? 'solid') as Garment['pattern'],
    material: (raw.material ?? 'other') as Garment['material'],
    seasons: Object.entries((raw.season_suitability ?? {}) as Record<string, boolean>)
      .filter(([, v]) => v)
      .map(([k]) => k as Garment['seasons'][number]),
    occasions: [],
    brand: String(raw.brand ?? ''),
    size: String(raw.size ?? ''),
    imageUrls: (raw.image_urls as string[]) ?? [],
    thumbnailUrl: ((raw.image_urls as string[]) ?? [])[0] ?? '',
    meshUrl: (raw.mesh_url as string) ?? null,
    purchaseLinks: [],
    timesWorn: Number(raw.times_worn ?? 0),
    lastWorn: null,
    isFavorite: false,
    notes: '',
    tags: (raw.tags as string[]) ?? [],
    costPerWear: null,
    purchasePrice: (raw.purchase_price as number) ?? null,
    purchaseDate: null,
    createdAt: String(raw.created_at ?? ''),
    updatedAt: String(raw.updated_at ?? ''),
  };
}

/** Map a backend GarmentListResponse to frontend PaginatedResponse<Garment>. */
function mapGarmentList(raw: Record<string, unknown>): PaginatedResponse<Garment> {
  return {
    items: ((raw.items ?? []) as Record<string, unknown>[]).map(mapGarment),
    total: Number(raw.total ?? 0),
    page: Number(raw.page ?? 1),
    pageSize: Number(raw.page_size ?? 20),
    hasMore: Boolean(raw.has_next ?? false),
  };
}

export const garmentService = {
  async list(
    params: GarmentFilters & { page?: number; pageSize?: number },
  ): Promise<PaginatedResponse<Garment>> {
    const { data } = await api.get('/closet/garments', { params });
    return mapGarmentList(data);
  },

  async getById(id: string): Promise<Garment> {
    const { data } = await api.get(`/closet/garments/${id}`);
    return mapGarment(data);
  },

  async upload(
    images: File[],
    video: File | null,
    metadata: Partial<Garment>,
    onProgress?: (progress: number) => void,
  ): Promise<Garment> {
    const formData = new FormData();

    images.forEach((img) => formData.append('images', img));
    if (video) formData.append('video', video);
    formData.append('metadata', JSON.stringify(metadata));

    const { data } = await uploadFormData('/closet/garments', formData, onProgress);
    return mapGarment(data);
  },

  async update(id: string, updates: Partial<Garment>): Promise<Garment> {
    const { data } = await api.put(`/closet/garments/${id}`, updates);
    return mapGarment(data);
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/closet/garments/${id}`);
  },

  async addImages(
    id: string,
    images: File[],
    onProgress?: (progress: number) => void,
  ): Promise<Garment> {
    const formData = new FormData();
    images.forEach((img) => formData.append('images', img));
    const { data } = await uploadFormData(
      `/closet/garments/${id}/images`,
      formData,
      onProgress,
    );
    return mapGarment(data);
  },

  async removeImage(id: string, imageUrl: string): Promise<Garment> {
    const { data } = await api.delete(`/closet/garments/${id}/images`, {
      data: { imageUrl },
    });
    return mapGarment(data);
  },

  async searchSimilar(garmentId: string): Promise<Garment[]> {
    const { data } = await api.get(`/closet/garments/${garmentId}/similar`);
    return ((data ?? []) as Record<string, unknown>[]).map(mapGarment);
  },

  async searchByImage(image: File): Promise<Garment[]> {
    const formData = new FormData();
    formData.append('image', image);
    const { data } = await uploadFormData('/closet/garments/search-by-image', formData);
    return ((data ?? []) as Record<string, unknown>[]).map(mapGarment);
  },

  /**
   * Warm up the client-side segmentation model.
   * Call early (e.g. on page load) so first segmentation is instant.
   */
  async warmUpClientSegmentation(
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    await warmUpSegmentation(onProgress);
  },

  /**
   * Detect and segment garments CLIENT-SIDE using SegFormer B0 (4.2 MB ONNX).
   * Runs entirely in the browser - no server round-trip needed.
   * Falls back to server-side detection on failure.
   */
  async detectAttributesClient(
    images: File[],
    onProgress?: (progress: number, stage: string) => void,
  ): Promise<GarmentDetectionResult> {
    try {
      // Process first image client-side with a 10s timeout
      const result = await Promise.race([
        segmentGarmentsClient(images[0], onProgress),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Segmentation timed out')), 10000),
        ),
      ]);

      return {
        garments: result.garments.map((g) => ({
          bbox: g.bbox,
          confidence: g.confidence,
          segmentImageBase64: g.segmentImageBase64,
          category: g.category,
          subCategory: g.subCategory,
          primaryColors: g.primaryColors.map((c) => ({
            hex: c.hex,
            name: '',
            rgb: c.rgb,
            percentage: c.percentage,
          })),
          pattern: 'solid',
          material: 'cotton',
          seasonSuitability: { spring: 0.7, summer: 0.7, fall: 0.7, winter: 0.7 },
        })) as DetectedGarment[],
        processingTimeMs: result.processingTimeMs,
      };
    } catch (error) {
      console.warn('Client-side segmentation failed, trying server:', error);
      // Quick server fallback with 5s timeout (for tests & when server is available).
      // If server also fails, the error propagates to the caller which shows a
      // default garment entry for the user to fill in manually.
      try {
        return await Promise.race([
          this.detectAttributes(images, (p) => onProgress?.(p, 'Server processing…')),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Server fallback timed out')), 5000),
          ),
        ]);
      } catch {
        throw error; // Rethrow original client-side error
      }
    }
  },

  /**
   * Detect and segment all garments in uploaded images (SERVER-SIDE).
   */
  async detectAttributes(
    images: File[],
    onProgress?: (progress: number) => void,
  ): Promise<GarmentDetectionResult> {
    const formData = new FormData();
    images.forEach((img) => formData.append('images', img));
    const { data } = await uploadFormData(
      '/closet/garments/detect-attributes',
      formData,
      onProgress,
    );

    // Map backend snake_case response to frontend camelCase types
    const result: GarmentDetectionResult = {
      garments: (data.garments ?? []).map((g: Record<string, unknown>) => ({
        bbox: g.bbox as [number, number, number, number],
        confidence: g.confidence as number,
        segmentImageBase64: g.segment_image_b64 as string,
        category: g.category as string,
        subCategory: g.sub_category as string,
        primaryColors: (
          (g.primary_colors as Array<Record<string, unknown>>) ?? []
        ).map((c) => ({
          hex: c.hex as string,
          name: (c.name as string) ?? '',
          rgb: c.rgb as number[],
          percentage: c.percentage as number,
        })),
        pattern: g.pattern as string,
        material: g.material as string,
        seasonSuitability: (g.season_suitability ?? {}) as Record<string, number>,
      })) as DetectedGarment[],
      processingTimeMs: (data.processing_time_ms as number) ?? 0,
    };

    return result;
  },

  /**
   * Convert a base64 segment image to a blob URL for display.
   */
  segmentImageToUrl(base64Png: string): string {
    if (!base64Png) return '';
    // Strip data URI prefix if present
    const raw = base64Png.replace(/^data:image\/[a-z]+;base64,/, '');
    try {
      const binary = atob(raw);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'image/png' });
      return URL.createObjectURL(blob);
    } catch {
      // If base64 decoding fails, try using it as a direct URL
      return base64Png.startsWith('http') || base64Png.startsWith('/')
        ? base64Png
        : '';
    }
  },

  async getOutfitHistory(garmentId: string): Promise<{ outfitId: string; date: string }[]> {
    const { data } = await api.get(`/closet/garments/${garmentId}/outfit-history`);
    return data;
  },

  /** Trigger 3D reconstruction for a single garment. */
  async reconstruct3D(garmentId: string): Promise<Garment> {
    const { data } = await api.post(`/closet/garments/${garmentId}/reconstruct`);
    return mapGarment(data);
  },

  /** Batch reconstruct all garments without a mesh. */
  async reconstructAll(): Promise<{ total: number; succeeded: number; failed: number }> {
    const { data } = await api.post('/closet/garments/reconstruct-all');
    return data;
  },
};
